import * as React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Phone,
  PlugZap,
  RefreshCw,
  Search,
  ShoppingCart,
  Trash2,
  Wand2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/toast';
import {
  useMyNumbers,
  useNumberSearch,
  usePurchaseNumber,
  useReconfigureNumberWebhook,
  useReleaseNumber,
  type AvailableNumberRow,
  type MyNumberRow,
  type NumberProvider,
} from '../../../hooks/useNumberShop';
import { useTwilioBundles, type BundleStatus } from '../../../hooks/useTwilioBundles';
import { useTestProviderCredentials } from '../../../hooks/useProviderConnectionTest';
import { useMyBilling } from '../../../hooks/useBilling';
import { formatCallableError } from '../../../lib/callableError';
import { cn } from '../../../lib/utils';

type Country = 'DE' | 'AT' | 'CH';
type NumberType = 'local' | 'mobile' | 'tollfree';

const COUNTRY_LABELS: Record<Country, string> = {
  DE: 'Deutschland',
  AT: 'Österreich',
  CH: 'Schweiz',
};

const TYPE_LABELS: Record<NumberType, string> = {
  local: 'Festnetz / Lokal',
  mobile: 'Mobil',
  tollfree: 'Gebührenfrei',
};

const PROVIDER_LABELS: Record<NumberProvider, string> = {
  twilio: 'Twilio',
  telnyx: 'Telnyx',
};

const PROVIDER_TAGLINES: Record<NumberProvider, string> = {
  twilio:
    'Twilio rechnet die Monatsmiete deinem Twilio-Account direkt ab — die Plattform berührt keine Zahlung.',
  telnyx:
    'Telnyx rechnet die Monatsmiete deinem Telnyx-Account direkt ab. Voice-Routing läuft über deine in Telnyx Mission Control angelegte Connection.',
};

/**
 * NOTE — the page-level `NumberShopSection` was removed: number buying
 * now lives inline under the "Anbieter & Nummer" tab so the user picks
 * a provider once and gets everything (config, search, compliance,
 * inventory) in a single flow. The `Provider*Numbers` exports below
 * are the building blocks that `ProviderTab` composes for whichever
 * provider is currently selected.
 *
 * Tagline strings kept exported so ProviderTab can show provider-specific
 * intro copy without duplicating the wording.
 */
export const NUMBER_PROVIDER_TAGLINES = PROVIDER_TAGLINES;

/** Search + buy + active inventory for Twilio. */
export function TwilioNumbersBlock() {
  return (
    <>
      <SearchCard provider="twilio" />
      <BundleStatusCard />
      <ComplianceCard />
      <MyNumbersCard provider="twilio" />
    </>
  );
}

/** Search + buy + active inventory for Telnyx. */
export function TelnyxNumbersBlock() {
  return (
    <>
      <SearchCard provider="telnyx" />
      <TelnyxNoteCard />
      <MyNumbersCard provider="telnyx" />
    </>
  );
}

/**
 * Compact "Verbindung prüfen" button used inside ProviderTab next to the
 * radio. Confirms the saved API credentials reach Twilio / Telnyx and
 * surfaces account-level metadata so the user can verify they're talking
 * to the right account.
 *
 * After a successful test the button stays in a green "Verbindung
 * geprüft" state so the user has persistent visual confirmation, not
 * just a transient toast. Clicking again retests. The success state
 * resets when the provider is switched (a Twilio-OK badge would lie
 * once the user toggled to Telnyx).
 */
export function ProviderConnectionTestButton({ provider }: { provider: NumberProvider }) {
  const test = useTestProviderCredentials();
  const { toast } = useToast();
  const [verifiedFor, setVerifiedFor] = React.useState<NumberProvider | null>(null);

  // Reset success state if the user switches provider — old confirmation
  // would no longer apply.
  React.useEffect(() => {
    if (verifiedFor && verifiedFor !== provider) setVerifiedFor(null);
  }, [provider, verifiedFor]);

  async function onTest() {
    try {
      const r = await test.mutateAsync(provider);
      const note =
        r.provider === 'twilio'
          ? `${r.friendlyName ?? 'OK'} · ${r.accountStatus ?? '—'} (${r.accountType ?? 'Account'})`
          : r.connectionConfigured
            ? 'API-Key OK · Connection-ID hinterlegt'
            : 'API-Key OK · noch keine Connection-ID';
      toast({ title: `${PROVIDER_LABELS[provider]} erreichbar`, description: note });
      setVerifiedFor(provider);
    } catch (err) {
      toast({
        title: `${PROVIDER_LABELS[provider]}-Verbindung fehlgeschlagen`,
        description: formatCallableError(err),
        variant: 'destructive',
      });
      setVerifiedFor(null);
    }
  }

  const verified = verifiedFor === provider;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={() => void onTest()}
      disabled={test.isPending}
      title={
        verified
          ? `${PROVIDER_LABELS[provider]}-Verbindung erfolgreich geprüft — klicken um erneut zu testen`
          : `${PROVIDER_LABELS[provider]}-API-Verbindung prüfen`
      }
      className={cn(
        verified &&
          'border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15 hover:text-emerald-200',
      )}
    >
      {test.isPending ? (
        <Loader2 className="mr-1.5 size-3.5 animate-spin" />
      ) : verified ? (
        <CheckCircle2 className="mr-1.5 size-3.5" />
      ) : (
        <PlugZap className="mr-1.5 size-3.5" />
      )}
      {verified ? 'Verbindung geprüft' : 'Verbindung prüfen'}
    </Button>
  );
}

// ── Search & Buy ────────────────────────────────────────────────────────

function SearchCard({ provider }: { provider: NumberProvider }) {
  const search = useNumberSearch();
  const { toast } = useToast();

  const [country, setCountry] = React.useState<Country>('DE');
  const [type, setType] = React.useState<NumberType>('local');
  const [areaCode, setAreaCode] = React.useState('');
  const [contains, setContains] = React.useState('');
  const [results, setResults] = React.useState<AvailableNumberRow[]>([]);
  const [purchasing, setPurchasing] = React.useState<AvailableNumberRow | null>(null);

  // Reset results when the provider changes — the inventory is provider-specific.
  React.useEffect(() => {
    setResults([]);
  }, [provider]);

  async function onSearch(e: React.FormEvent) {
    e.preventDefault();
    try {
      const r = await search.mutateAsync({
        provider,
        country,
        type,
        areaCode: areaCode.trim() || undefined,
        contains: contains.trim() || undefined,
        voiceEnabled: true,
        limit: 20,
      });
      setResults(r.items);
      if (r.items.length === 0) {
        toast({
          title: 'Keine Treffer',
          description: 'Filter lockern oder anderen Vorwahlbereich versuchen.',
        });
      }
    } catch (err) {
      toast({
        title: 'Suche fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Search className="size-4" />
          Verfügbare Nummern suchen ({PROVIDER_LABELS[provider]})
        </CardTitle>
        <CardDescription>
          Live-Suche im Inventar deines {PROVIDER_LABELS[provider]}-Accounts. Vorwahl
          und Inhalt sind optional.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSearch} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Land</Label>
              <Select value={country} onValueChange={(v) => setCountry(v as Country)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DE">{COUNTRY_LABELS.DE}</SelectItem>
                  <SelectItem value="AT">{COUNTRY_LABELS.AT}</SelectItem>
                  <SelectItem value="CH">{COUNTRY_LABELS.CH}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Typ</Label>
              <Select value={type} onValueChange={(v) => setType(v as NumberType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">{TYPE_LABELS.local}</SelectItem>
                  <SelectItem value="mobile">{TYPE_LABELS.mobile}</SelectItem>
                  <SelectItem value="tollfree">{TYPE_LABELS.tollfree}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="area-code">Vorwahl (optional)</Label>
              <Input
                id="area-code"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="z. B. 30"
                maxLength={6}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contains">Enthält (optional)</Label>
              <Input
                id="contains"
                value={contains}
                onChange={(e) => setContains(e.target.value.replace(/[^\d*]/g, ''))}
                placeholder="z. B. 12*34"
                maxLength={12}
              />
            </div>
          </div>

          <Button type="submit" disabled={search.isPending}>
            {search.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            Suchen
          </Button>
        </form>

        {results.length > 0 && (
          <div className="mt-5 space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {results.length} Treffer
            </div>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Nummer</th>
                    <th className="px-3 py-2">Region</th>
                    <th className="px-3 py-2">Voice</th>
                    <th className="px-3 py-2">SMS</th>
                    {provider === 'telnyx' && <th className="px-3 py-2">€/Monat</th>}
                    {provider === 'twilio' && <th className="px-3 py-2">Compliance</th>}
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((n) => (
                    <tr
                      key={n.phoneNumber}
                      className="border-b border-border/50 last:border-0"
                    >
                      <td className="px-3 py-2 font-mono">{n.phoneNumber}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {n.locality ?? n.region ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        {n.capabilities.voice ? (
                          <CheckCircle2 className="size-4 text-emerald-400" />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {n.capabilities.sms ? (
                          <CheckCircle2 className="size-4 text-emerald-400" />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      {provider === 'telnyx' && (
                        <td className="px-3 py-2 text-xs tabular-nums">
                          {n.monthlyCost
                            ? `${n.monthlyCost} ${n.currency ?? ''}`.trim()
                            : '—'}
                        </td>
                      )}
                      {provider === 'twilio' && (
                        <td className="px-3 py-2 text-xs">
                          {n.addressRequirements === 'none' ? (
                            <Badge variant="success">keine</Badge>
                          ) : (
                            <Badge variant="warning">{n.addressRequirements}</Badge>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPurchasing(n)}
                          disabled={!n.capabilities.voice}
                          title={!n.capabilities.voice ? 'Nicht Voice-fähig' : 'Kaufen'}
                        >
                          <ShoppingCart className="mr-1 h-3.5 w-3.5" />
                          Kaufen
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {purchasing && (
          <PurchaseDialog
            provider={provider}
            number={purchasing}
            onClose={() => setPurchasing(null)}
            onPurchased={() => {
              setPurchasing(null);
              setResults((rs) => rs.filter((r) => r.phoneNumber !== purchasing.phoneNumber));
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function PurchaseDialog({
  provider,
  number,
  onClose,
  onPurchased,
}: {
  provider: NumberProvider;
  number: AvailableNumberRow;
  onClose: () => void;
  onPurchased: () => void;
}) {
  const purchase = usePurchaseNumber();
  const { toast } = useToast();
  const [setAsActive, setSetAsActive] = React.useState(true);

  async function onConfirm() {
    try {
      const r = await purchase.mutateAsync({
        provider,
        phoneNumber: number.phoneNumber,
        setAsActive,
      });
      toast({
        title: 'Nummer gekauft',
        description: `${r.phoneNumber} ist jetzt mit dem Voice-Agent verbunden.`,
      });
      onPurchased();
    } catch (err) {
      toast({
        title: 'Kauf fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nummer kaufen ({PROVIDER_LABELS[provider]})</DialogTitle>
          <DialogDescription>
            Bestätige, dass diese Nummer auf deinem {PROVIDER_LABELS[provider]}-Account
            gekauft werden soll.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/20 p-3">
            <div className="font-mono text-lg">{number.phoneNumber}</div>
            <div className="text-xs text-muted-foreground">
              {number.country} · {number.locality ?? number.region ?? '—'}
              {number.monthlyCost && (
                <>
                  {' · '}
                  {number.monthlyCost} {number.currency ?? ''}/Monat
                </>
              )}
            </div>
          </div>
          <div className="space-y-2 rounded-md bg-amber-500/10 p-3 text-xs text-amber-200">
            <div className="font-medium">Bitte beachten</div>
            <ul className="list-inside list-disc space-y-0.5">
              <li>
                {PROVIDER_LABELS[provider]} belastet ab sofort deinem Account die monatliche
                Nummern-Miete plus die ggf. einmalige Setup-Gebühr — die Plattform reicht
                nichts weiter.
              </li>
              {provider === 'twilio' && (
                <li>
                  Webhook-Konfiguration (Voice-URL + Status-Callback) wird automatisch auf
                  den CP-3 Voice-Agent gesetzt.
                </li>
              )}
              {provider === 'telnyx' && (
                <li>
                  Die Nummer wird automatisch deiner in Telnyx hinterlegten Voice-Connection
                  zugewiesen — das Routing zum CP-3 Voice-Agent läuft darüber.
                </li>
              )}
              {provider === 'twilio' && number.addressRequirements !== 'none' && (
                <li>
                  Twilio verlangt für diese Nummer Compliance-Dokumente
                  („{number.addressRequirements}"). Ohne hinterlegtes Bundle kann der Kauf
                  scheitern oder die Nummer wird gesperrt.
                </li>
              )}
            </ul>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={setAsActive}
              onChange={(e) => setSetAsActive(e.target.checked)}
            />
            Diese Nummer als aktive Nummer im Telefon-Reiter setzen
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={purchase.isPending}>
            Abbrechen
          </Button>
          <Button onClick={onConfirm} disabled={purchase.isPending}>
            {purchase.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ShoppingCart className="mr-2 h-4 w-4" />
            )}
            Kaufen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Compliance hint (Twilio) ────────────────────────────────────────────

// Brief Section 8.2: nur in DACH einblenden — Twilio Regulatory Bundles
// gelten ausschließlich für DE/AT/CH. Bei anderen Sitzländern blendet
// die Karte sich komplett aus, statt den Nutzer mit Doku zu erschlagen
// die er nicht braucht.
function ComplianceCard() {
  const billing = useMyBilling();
  const country = billing.data?.address?.country?.toUpperCase();
  if (country && country !== 'DE' && country !== 'AT' && country !== 'CH') {
    return null;
  }
  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="size-4 text-amber-400" />
          Compliance-Hinweis für DE / AT / CH (Twilio)
        </CardTitle>
        <CardDescription>
          Lokal- und Mobil-Nummern in der DACH-Region erfordern bei Twilio einen
          Regulatory Bundle. Ohne hinterlegtes Bundle kann der Kauf abgelehnt oder
          die Nummer nachträglich gesperrt werden.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5 text-sm">
          <li className="flex gap-2">
            <span className="text-amber-400">•</span>
            <span>
              <strong>Deutschland (Lokal/Mobil):</strong> Handelsregisterauszug oder
              Gewerbeanmeldung + Adressnachweis (kein P.O. Box). Seit September 2025
              auch USt-ID statt BRN möglich.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-amber-400">•</span>
            <span>
              <strong>Österreich:</strong> Firmenbuchauszug oder Gewerbeschein,
              Adressnachweis am Standort.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-amber-400">•</span>
            <span>
              <strong>Schweiz:</strong> Handelsregisterauszug, Adressnachweis.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-amber-400">•</span>
            <span>
              Bundle wird in der Twilio-Console hochgeladen — Review durch Twilio dauert
              1–5 Werktage.
            </span>
          </li>
        </ul>
        <Button asChild variant="outline" size="sm" className="mt-3">
          <a
            href="https://console.twilio.com/us1/develop/phone-numbers/regulatory-compliance/bundles"
            target="_blank"
            rel="noreferrer"
          >
            Bundle in Twilio-Console anlegen
            <ExternalLink className="ml-2 size-3.5" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Telnyx-spezifischer Hinweis ─────────────────────────────────────────

function TelnyxNoteCard() {
  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="size-4 text-amber-400" />
          Telnyx Voice-Connection erforderlich
        </CardTitle>
        <CardDescription>
          Telnyx routet Voice-Anrufe nicht über einzelne Webhook-URLs, sondern über
          „Connections". Du brauchst eine Voice-Connection in Telnyx Mission Control.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5 text-sm">
          <li className="flex gap-2">
            <span className="text-amber-400">•</span>
            <span>
              In Telnyx Mission Control: <strong>Voice → Connections → Add</strong>{' '}
              (TeXML oder SIP). Media-Fork-URL auf den CP-3-Voice-Agent setzen.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-amber-400">•</span>
            <span>
              Die <strong>Connection-ID</strong> trägst du dann im Telefon-Reiter unter
              „Telnyx" ein. Erst dann kann hier gekauft werden.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-amber-400">•</span>
            <span>
              Compliance-Dokumente (HRB, Adresse) werden für DACH ebenfalls bei Telnyx
              direkt hinterlegt.
            </span>
          </li>
        </ul>
        <Button asChild variant="outline" size="sm" className="mt-3">
          <a
            href="https://portal.telnyx.com/#/app/connections"
            target="_blank"
            rel="noreferrer"
          >
            Telnyx Mission Control öffnen
            <ExternalLink className="ml-2 size-3.5" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Active numbers ─────────────────────────────────────────────────────

function MyNumbersCard({ provider }: { provider: NumberProvider }) {
  const numbers = useMyNumbers(provider);
  const reconfigure = useReconfigureNumberWebhook();
  const release = useReleaseNumber();
  const { toast } = useToast();

  async function onReconfigure(n: MyNumberRow) {
    try {
      await reconfigure.mutateAsync({ provider, numberRef: n.numberRef });
      toast({ title: 'Routing neu gesetzt', description: n.phoneNumber });
    } catch (err) {
      toast({
        title: 'Reconfigure fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  async function onRelease(n: MyNumberRow) {
    if (
      !confirm(
        `Nummer ${n.phoneNumber} wirklich freigeben? ${PROVIDER_LABELS[provider]} stoppt die monatliche Abrechnung. Die Nummer geht zurück in den Pool und kann später nicht mehr zurückgeholt werden.`,
      )
    ) {
      return;
    }
    try {
      await release.mutateAsync({ provider, numberRef: n.numberRef });
      toast({ title: 'Nummer freigegeben', description: n.phoneNumber });
    } catch (err) {
      toast({
        title: 'Freigabe fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const items = numbers.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Phone className="size-4" />
          Aktive Nummern ({PROVIDER_LABELS[provider]})
        </CardTitle>
        <CardDescription>
          {provider === 'twilio'
            ? '„Webhook neu setzen" zwingt die CP-3-Voice-URL erneut auf, falls extern überschrieben.'
            : '„Routing neu setzen" hängt die Nummer wieder an deine konfigurierte Telnyx-Connection.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {numbers.isLoading ? (
          <div className="flex h-20 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
          </div>
        ) : numbers.error ? (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {(numbers.error as Error).message}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Keine Nummern auf diesem {PROVIDER_LABELS[provider]}-Account.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((n) => (
              <div
                key={n.numberRef}
                className={cn(
                  'flex flex-wrap items-center gap-3 rounded-md border border-border bg-card p-3',
                  !n.webhookOwned && 'border-amber-500/40 bg-amber-500/5',
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm">{n.phoneNumber}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    {n.webhookOwned ? (
                      <Badge variant="success">CP-3 Voice verbunden</Badge>
                    ) : (
                      <Badge variant="warning">
                        {provider === 'twilio'
                          ? 'fremder Webhook'
                          : 'falsche Connection'}
                      </Badge>
                    )}
                    {n.capabilities.voice && <Badge variant="muted">Voice</Badge>}
                    {n.capabilities.sms && <Badge variant="muted">SMS</Badge>}
                    {n.bundleSid && <Badge variant="muted">Bundle ✓</Badge>}
                    {n.connectionId && (
                      <Badge variant="muted" className="font-mono">
                        {n.connectionId.slice(0, 10)}…
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReconfigure(n)}
                  disabled={reconfigure.isPending}
                  title={
                    provider === 'twilio'
                      ? 'Webhook neu auf CP-3 Voice setzen'
                      : 'Nummer wieder an konfigurierte Connection binden'
                  }
                >
                  {reconfigure.isPending ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="mr-1 h-3.5 w-3.5" />
                  )}
                  {provider === 'twilio' ? 'Webhook setzen' : 'Routing setzen'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onRelease(n)}
                  disabled={release.isPending}
                  title={`Nummer freigeben (${PROVIDER_LABELS[provider]}-Billing stoppt)`}
                >
                  <Trash2 className="mr-1 h-3.5 w-3.5" />
                  Freigeben
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => numbers.refetch()}
            disabled={numbers.isFetching}
          >
            <RefreshCw className={cn('mr-1 h-3.5 w-3.5', numbers.isFetching && 'animate-spin')} />
            Aktualisieren
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Twilio Bundle status ────────────────────────────────────────────────

const BUNDLE_STATUS_LABEL: Record<BundleStatus, string> = {
  draft: 'Entwurf',
  'pending-review': 'Wartet auf Review',
  'in-review': 'Im Review',
  'twilio-rejected': 'Abgelehnt',
  'twilio-approved': 'Approved',
  'provisionally-approved': 'Provisorisch ok',
};

function bundleStatusVariant(s: BundleStatus): 'success' | 'warning' | 'destructive' | 'muted' {
  if (s === 'twilio-approved' || s === 'provisionally-approved') return 'success';
  if (s === 'pending-review' || s === 'in-review') return 'warning';
  if (s === 'twilio-rejected') return 'destructive';
  return 'muted';
}

/** Card rendered above the generic Compliance hint — shows the user's
 *  actual bundle inventory with status badges. */
function BundleStatusCard() {
  const bundles = useTwilioBundles(true);
  const items = bundles.data ?? [];
  const approvedCount = items.filter(
    (b) => b.status === 'twilio-approved' || b.status === 'provisionally-approved',
  ).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle2 className="size-4" />
          Regulatory Bundles
        </CardTitle>
        <CardDescription>
          Live-Status deiner bei Twilio hinterlegten Compliance-Bundles. Approved-Bundles werden
          beim Kauf von DE/AT/CH-Lokal- oder Mobil-Nummern automatisch herangezogen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {bundles.isLoading ? (
          <div className="flex h-16 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 size-3.5 animate-spin" /> Lade …
          </div>
        ) : bundles.error ? (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {(bundles.error as Error).message}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            Keine Bundles im Twilio-Account. Lege eines in der Twilio-Console an, bevor du eine
            DACH-Lokal- oder Mobil-Nummer kaufst.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {approvedCount} von {items.length} approved
            </div>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Bundle</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Erstellt</th>
                    <th className="px-3 py-2">Gültig bis</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((b) => (
                    <tr key={b.sid} className="border-b border-border/50 last:border-0">
                      <td className="px-3 py-2">
                        <div className="font-medium">{b.friendlyName || '—'}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">{b.sid}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={bundleStatusVariant(b.status)}>
                          {BUNDLE_STATUS_LABEL[b.status] ?? b.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(b.dateCreated).toLocaleDateString('de-DE')}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {b.validUntil
                          ? new Date(b.validUntil).toLocaleDateString('de-DE')
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="mt-3 flex items-center justify-between">
          <Button asChild variant="ghost" size="sm">
            <a
              href="https://console.twilio.com/us1/develop/phone-numbers/regulatory-compliance/bundles"
              target="_blank"
              rel="noreferrer"
            >
              In Twilio-Console verwalten
              <ExternalLink className="ml-2 size-3.5" />
            </a>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => bundles.refetch()}
            disabled={bundles.isFetching}
          >
            <RefreshCw className={cn('mr-1 size-3.5', bundles.isFetching && 'animate-spin')} />
            Aktualisieren
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
